import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { sendMessage2ServiceWorker } from './util/service_worker';
import './App.css';
import Footer from './components/Footer';
// components
import Nav from './components/Nav';
import Notifier from './components/Notifier';
import Account from './pages/Account';
import Accounts from './pages/Admin/Accounts';
import AccountsInfo from './pages/Admin/AccountsInfo';
import Cases from './pages/Admin/Cases';
import Clinics from './pages/Admin/Clinics';
import Countries from './pages/Admin/Countries';
import SubDomains from './pages/Admin/SubDomains';
//admin pages
import Dashboard from './pages/Admin/Dashboard';
import Groups from './pages/Admin/Groups';
import DistrGroups from './pages/Admin/DistrGroups';
import AdminGroups from './pages/Admin/AdminGroups';
import RefinementLogs from './pages/Admin/RefinementLogs';
import Reports from './pages/Admin/Reports';
import Settings from './pages/Admin/Settings';
import ChangePassword from './pages/changePassword';
import changePasswordAfterLogin from './pages/changePasswordAfterLogin';
// import Simulation from './components/3dSimulation/Simulation'
import ClinicalSimulation from './pages/ClinicalSimulation';
import MeditReview from './pages/MeditReview';
import EmailLog from './pages/EmailLog';
import Forgot from './pages/Forgot';
import Login from './pages/Login';
import PortalLogin from './pages/PortalLogin';
import ThirdPartyLogin from './pages/ThirdPartyLogin';
import NewPatient from './pages/NewPatient';
import MockupOrdering from './pages/MockupOrdering';
import RestorativePhaseCaseIntake from './pages/RestorativePhaseCaseIntake/RestorativePhaseCaseIntake';
import PatientInfo from './pages/PatientInfo';
// pages
import Patients from './pages/Patients';
import NewPatientPrintData from './pages/Print/NewPatientPrintData';
import Refinement from './pages/Refinement';
import Register from './pages/Register';
import RegisterThirdParty from './pages/RegisterThirdParty';
import Support from './pages/Support';
import TreatmentApproval from './pages/TreatmentApproval';
import { checkLoggedIn } from './store/Auth/Auth.action';
import * as configAction from './store/config/config.action';
import { usePrevious } from './util/react';
import Maintenance from './pages/maintenance';
import { loadRes } from './store/res/res.action';
import EmailManagement from './pages/Admin/EmailManagement';
import ProductMatrix from './pages/Admin/ProductMatrix';
import SelfDesignPricing from './pages/Admin/SelfDesignPricing';

import SupportResource from './pages/Admin/SupportResource';
import FormDownload from './pages/Admin/FormDownload';
import KnowledgeBase from './pages/Admin/KnowledgeBase';
import DistrMsg from './pages/Admin/DistrMsg';
import CasesLog from './pages/Admin/CasesLog';
import CasesRollbackLog from './pages/Admin/CasesRollbackLog';
import TraceLog from './pages/Admin/TraceLog';
import CasesTransferLog from './pages/Admin/CasesTransferLog';
import SystemAnnouncement from './pages/Admin/SystemAnnouncement';
import DiffRetainers from './pages/DiffRetainer/DiffRetainer';
import SlowMovingCases from './pages/Admin/SlowMovingCases';
import useUserAllowedFeatures from './hooks/useUserAllowedFeatures';
import ThreeShape from './pages/threeShape';
import AppSetting from './pages/AppSetting';
import AppSettingApproval from './pages/AppSettingApproval';
import { CasesActivityLog } from './pages/Admin/CasesActivityLog';
// import RetrackReport from './components/NewPatient/Retrack/RetrackReport/RetrackReport';
import GlobalDialog from './components/Dialog/GlobalDialog';
import { useFetchRetainerReqNeededData } from './pages/hooks/DiffRetainerHooks/useFetchRetainerReqNeededData';
import { WebSocketContext } from './context/Context';
import useWs from './hooks/useWs';
import { useVersionCheck } from './hooks/useVersionCheck';

import useExcludeResourceApi from './hooks/useExcludeResouceApi';
import Home from './pages/Home';
import { Box } from '@material-ui/core';
import AfterLoginNotification from './components/AfterLoginNotification';
import useAfterLoginNotification from './hooks/useAfterLoginNotification';
const MaintenanceMode = false;

const theme = createMuiTheme({
    palette: {
        primary: {
            main: '#fff',
        },
        secondary: {
            main: '#445994',
        },
    },
    typography: {
        // Use the system font instead of the default Roboto font.
        // fontFamily: 'Museo',
        body1: {
            fontWeight: 500,
            fontFamily: "'Montserrat', sans-serif"
        },
        subtitle1: {
            fontSize: 12,
        },
        button: {
            fontSize: '1.0rem',
            textTransform: 'none',
        },
        h6: {
            fontFamily: "'Montserrat', sans-serif",
        }
    },
});

export const RES_EXCLUSION = ['/clinicalSimulation'];

export default function App(props) {
    const dispatch = useDispatch(),
        history = useHistory(),
        location = useLocation(),
        { isAuthenticated, res, lang, config, user } = useSelector((store) => ({
            isAuthenticated: store.auth.isAuthenticated,
            config: store.config,
            res: store.res,
            lang: store.lang,
            user: store.auth.user,
        }));

    const [loading, setLoading] = useState(true);
    const nav = useRef(null),
        footer = useRef(null),
        resLoadRetryCount = useRef(0),
        loadingRes = useRef(false);
    const prevIsAuth = usePrevious(isAuthenticated),
        prevRes = usePrevious(res);

    useFetchRetainerReqNeededData(location);
    const { wsManager } = useWs();
    const { isExcludeResouceApi } = useExcludeResourceApi();

    useUserAllowedFeatures();
    useVersionCheck();
    
    useEffect(() => {
        (async () => {
            try {
                updateNavAndFooter();
                await dispatch(
                    configAction.check({ res, config, lang, isAuthenticated, location, isExcludeResouceApi })
                );
                await dispatch(checkLoggedIn());
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (prevIsAuth === false && prevIsAuth !== isAuthenticated) {
            dispatch(configAction.loadConfig());
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        if (!loadingRes.current && !res.isInit) {
            loadingRes.current = true;
            (async () => {
                if (
                    (!res.countries ||
                        !Object.keys(res.countries).length ||
                        !res.stateProvinces ||
                        !Object.keys(res.stateProvinces).length) &&
                    ++resLoadRetryCount.current < 100
                ) {
                    if (isAuthenticated) {
                        if (!isExcludeResouceApi) {
                            await loadRes(isAuthenticated)(dispatch);
                        }
                    }
                    resLoadRetryCount.current = 0;
                }
                loadingRes.current = false;
            })();
        }
    }, [res, dispatch, resLoadRetryCount, loadingRes, isAuthenticated, isExcludeResouceApi]);

    const routes = useMemo(() => {
        if (MaintenanceMode) {
            return <Route component={Maintenance} />;
        } else {
            return (
                <Switch>
                    {(isAuthenticated === null || isAuthenticated) && !loading ? (
                        <>
                            <Route exact path="/" component={Patients} />
                            {/* <Route exact path='/mytest' component={RetrackReport} /> */}
                            <Route exact path="/patients/newPatient/:type/:caseTypeOption?" component={NewPatient} />
                            {/* <Route exact path='/patients/:patientsid/:id/View' component={PatientInfo} /> */}
                            <Route exact path="/patients/:id/mockup-ordering" component={MockupOrdering} />
                            <Route exact path="/patients/:id/restorative-phase-case-intake" component={RestorativePhaseCaseIntake} />
                            <Route exact path="/patients/:id" component={PatientInfo} />
                            <Route
                                exact
                                path="/patients/:patientsid/newCase/:type/:caseTypeOption?"
                                component={() => <NewPatient {...props} caseAction="newCaseSamePatient" />}
                            />
                            <Route exact path="/patients/:patientsid/samePatient/:type/:caseTypeOption?" component={() => <NewPatient {...props} caseAction="newCaseSamePatient" />} />
                            <Route exact path="/patients/:id/treatment" component={TreatmentApproval} />
                            <Route exact path="/patients/:id/refinement" component={Refinement} />
                            <Route exact path="/patients/:id/differentRatiner" component={DiffRetainers} />
                            <Route exact path="/patients/:id/defect_resubmit" component={Refinement} />
                            <Route exact path="/changePassword/:oldPassword?" component={ChangePassword} />
                            <Route
                                exact
                                path="/changePasswordAfterLogin/:oldPassword?"
                                component={changePasswordAfterLogin}
                            />
                            <Route
                                exact
                                path="/patients/:patientsid/:id/edit/:caseTypeOption?"
                                component={NewPatient}
                            />
                            <Route exact path="/patients/:id/edit/:caseTypeOption?" component={NewPatient} />
                            <Route path="/account/:tabIndex?" component={Account} />
                            <Route path="/support" component={Support} />
                            <Route path="/appSetting/:id?" component={AppSetting} />
                            <Route path="/appSettingApproval" component={AppSettingApproval} />
                            {/* <Route path='/sim/test' component={Simulation} /> */}
                            <Route exact path="/clinicalSimulation/:id/plan/:plan_id?" component={ClinicalSimulation} />
                            <Route exact path="/clinicalSimulation/:id" component={ClinicalSimulation} />
                            <Route exact path="/meditReview/:id" component={MeditReview} />
                            {/* <Route exact path='/clinicalSimulation2/:id' component={Viewer3D} /> */}
                            {/* <Route exact path='/clinicalSimulation/:id/2/:plan_id?' component={Viewer3D} /> */}
                            <Route exact path="/printData/:id" component={NewPatientPrintData} />
                            <Route path="/admin/dashboard" component={Dashboard} />
                            <Route exact path="/admin/accounts" component={Accounts} />
                            <Route path="/admin/accounts/:id/:tabIndex?" component={AccountsInfo} />
                            <Route path="/admin/orders" component={Dashboard} />
                            <Route path="/admin/cases" component={Cases} />
                            <Route path="/admin/distrgroups" component={DistrGroups} />
                            <Route path="/admin/admingroups" component={AdminGroups} />
                            <Route path="/admin/clinics" component={Clinics} />
                            <Route path="/admin/groups" component={Groups} />
                            <Route path="/admin/countries" component={Countries} />
                            {/* <Route path='/admin/subdomains' component={SubDomains} /> */}
                            <Route path="/admin/reports" component={Reports} />
                            <Route path="/admin/email-management" component={EmailManagement} />
                            <Route path="/admin/productmatrix" component={ProductMatrix} />
                            <Route path="/admin/self-design-pricing" component={SelfDesignPricing} />
                            <Route path="/admin/settings" component={Settings} />
                            <Route path="/admin/supportresource" component={SupportResource} />
                            <Route path="/admin/FormDownload" component={FormDownload} />
                            <Route path="/admin/KnowledgeBase" component={KnowledgeBase} />
                            <Route path="/admin/casesLog" component={CasesLog} />
                            <Route path="/admin/casesActivityLog" component={CasesActivityLog} />
                            <Route path="/admin/casesRollbackLog" component={CasesRollbackLog} />
                            <Route path="/admin/traceLog" component={TraceLog} />
                            <Route path="/admin/CasesTransferLog" component={CasesTransferLog} />
                            <Route path="/admin/DistrMsg" component={DistrMsg} />
                            <Route path="/admin/SystemAnnouncement" component={SystemAnnouncement} />
                            <Route path="/email/log/:logId" component={EmailLog} />
                            <Route path="/admin/refinementLog" component={RefinementLogs} />
                            <Route path="/admin/slow_moving_cases" component={SlowMovingCases} />

                            <Route exact path="/threeshape_connect/:success" component={ThreeShape} />
                            <Route exact path="/home" component={Home} />
                        </>
                    ) : null}
                    <Route exact path="/changePassword" component={ChangePassword} />
                    <Route exact path="/changePassword/:email/:oldPassword?" component={ChangePassword} />
                    <Route exact path="/register" component={Register} />
                    {/* <Route exact path='/promo_register' component={RegisterThirdParty} /> */}
                    <Route exact path="/forgot" component={Forgot} />
                    <Route exact path="/login" component={() => <Login isLoading={loading} />} />
                    <Route exact path="/loginFromMeditLink" component={PortalLogin} />
                    <Route exact path="/loginFromThirdParty" component={ThirdPartyLogin} />
                    {isAuthenticated === false ? (
                        <Route>
                            <Redirect
                                to={`/login${location.pathname && location.pathname.length > 1 ? `?redirect=${encodeURIComponent(location.pathname + location.search)}` : ''}`}
                            />
                        </Route>
                    ) : null}
                </Switch>
            );
        }
    }, [isAuthenticated, loading]);

    useEffect(() => {
        sendMessage2ServiceWorker({
            type: isAuthenticated ? 'login' : 'logout',
            user_id: parseInt(JSON.parse(localStorage.getItem('auth-token'))),
        });
    }, [isAuthenticated, loading]);

    useEffect(() => {
        if (isAuthenticated !== null) updateNavAndFooter();
        if (isAuthenticated && history.location.pathname.startsWith('/login')) {
            const redirect = history.location.search;
            if (redirect && redirect.length > 0 && redirect.indexOf('redirect') > -1) {
                history.replace(decodeURIComponent(redirect.slice(redirect.indexOf('redirect=') + 'redirect='.length)));
            } else {
                history.replace('/');
            }
        }
        dispatch(configAction.check({ res, lang, config, isAuthenticated, location, isExcludeResouceApi }));
    }, [isAuthenticated, history, isExcludeResouceApi]);

    const updateNavAndFooter = () => {
        const navExclusion = [
            '/admin',
            '/printData',
            '/clinicalSimulation2',
            '/loginFromMeditLink',
            '/loginFromThirdParty',
            '/threeshape_connect',
            '/clinicalSimulation',
        ];
        const footerExclustion = [
            '/admin',
            '/clinicalSimulation2',
            '/loginFromMeditLink',
            '/loginFromThirdParty',
            '/threeshape_connect',
            '/clinicalSimulation',
            '/meditReview',
        ];
        if (navExclusion.some((ex) => history.location.pathname.startsWith(ex)) || !isAuthenticated) {
            nav.current = null;
        } else {
            nav.current = isAuthenticated !== null && <Nav />;
        }
        if (footerExclustion.some((ex) => history.location.pathname.startsWith(ex))) {
            footer.current = null;
        } else {
            footer.current = isAuthenticated !== null && <Footer />;
        }
    };
    const {
        open: afterLoginPopupOpen,
        notification: afterLoginNotification,
        handleClose: handleAfterLoginPopupClose,
    } = useAfterLoginNotification();
    return (
        <WebSocketContext.Provider value={wsManager}>
            <Notifier />
            <MuiThemeProvider theme={theme}>
                <Box
                    style={{
                        minHeight: '100svh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box>{nav.current}</Box>
                    <Box
                        style={{
                            flex: 1,
                        }}
                    >
                        {routes}
                    </Box>
                    <Box>{footer.current}</Box>
                </Box>
                <GlobalDialog />
                <AfterLoginNotification
                    notification={afterLoginNotification}
                    open={afterLoginPopupOpen}
                    onClose={handleAfterLoginPopupClose}
                />
            </MuiThemeProvider>
        </WebSocketContext.Provider>
    );
}
